import React, { useEffect, useState } from "react";
import { Box, Skeleton } from "@mui/material";
import AmbassadorHelper from "../../utils/AmbassadorHelper";

const CREATOR_ROLE_ID = 2;
const SCRIPT_MARKER = "data-follow-creator-client";
const WIDGET_TAG = "follow-creator-client";
const WIDGET_TIMEOUT = 4000;
const TRIGGER_W = 104;
const TRIGGER_H = 40;
const SUBTITLE = "Get an email when they post a new fit or drop a new collection.";

const resolveEnv = () => {
    const phpEnvName = typeof window !== "undefined" ? window?.TVSite?.phpEnvName : null;
    return phpEnvName === "local" ? "test" : phpEnvName;
};

const widgetScriptUrl = (env) => {
    const host = env === "app" ? "follow-creator.creatable.io" : `follow-creator.${env}.creatable.io`;
    return `https://${host}/assets/js/follow-creator-client.min.js`;
};

const followConfigUrl = (env, clientId, userId) => {
    const host = env === "app" ? "app.creatable.io" : `${env}.creatable.io`;
    return `https://${host}/customer/v1/api/customers/follow-config` +
        `?client_id=${clientId}&user_id=${userId}&role_id=${CREATOR_ROLE_ID}`;
};

const FOLLOW_AVATAR_PX = 256;

const FollowCreator = ({ ambassador, displayName, onFollowersChange }) => {
    const [config, setConfig] = useState(null);
    const [loading, setLoading] = useState(true);
    const [widgetReady, setWidgetReady] = useState(false);

    const env = resolveEnv();
    const clientId = typeof window !== "undefined" ? window?.TVSite?.loginId : null;
    const userId = ambassador?.id;
    const enabled = config?.enabled === true;

    useEffect(() => {
        if (!env || !clientId || !userId) {
            return undefined;
        }

        let active = true;
        fetch(followConfigUrl(env, clientId, userId))
            .then((response) => (response.ok ? response.json() : null))
            .then((data) => {
                if (active) {
                    setConfig(data);
                    setLoading(false);
                    if (typeof onFollowersChange === "function") {
                        onFollowersChange(data?.followers ?? 0);
                    }
                }
            })
            .catch(() => {
                if (active) {
                    setConfig(null);
                    setLoading(false);
                }
            });

        return () => {
            active = false;
        };
    }, [env, clientId, userId]);

    useEffect(() => {
        if (!enabled || !env || typeof document === "undefined") {
            return undefined;
        }
        if (document.querySelector(`script[${SCRIPT_MARKER}]`)) {
            return undefined;
        }

        const script = document.createElement("script");
        script.src = widgetScriptUrl(env);
        script.defer = true;
        script.setAttribute(SCRIPT_MARKER, "1");
        document.body.appendChild(script);

        return () => {
            if (script.parentNode) {
                script.parentNode.removeChild(script);
            }
        };
    }, [enabled, env]);

    useEffect(() => {
        if (!enabled || typeof window === "undefined" || !window.customElements) {
            return undefined;
        }

        let active = true;
        const settle = () => {
            if (active) {
                setWidgetReady(true);
            }
        };

        window.customElements.whenDefined(WIDGET_TAG).then(settle);
        const timer = window.setTimeout(settle, WIDGET_TIMEOUT);

        return () => {
            active = false;
            window.clearTimeout(timer);
        };
    }, [enabled]);

    const placeholder = (
        <Skeleton
            data-testid="tvp-follow-creator-placeholder"
            variant="rounded"
            animation="wave"
            width={TRIGGER_W}
            height={TRIGGER_H}
            sx={{ borderRadius: "999px", flex: "0 0 auto" }}
        />
    );

    if (loading) {
        return placeholder;
    }

    if (!enabled) {
        return null;
    }

    return (
        <Box
            display="flex"
            alignItems="center"
            data-testid="tvp-follow-creator"
            sx={{
                "--fcc-font": '"Visuelt Pro", Helvetica, Arial, Roboto, sans-serif',
                "--fcc-trigger-bg": "#0B1F3F",
                "--fcc-trigger-ink": "#FFFFFF",
                "--fcc-trigger-border": "1px solid #0B1F3F",
                "--fcc-trigger-radius": "999px",
                "--fcc-trigger-pad": "0 22px",
                "--fcc-trigger-h": "40px",
                "--fcc-trigger-font": "14px",
                "--fcc-trigger-weight": "600",
                "--fcc-trigger-hover-bg": "#1C3A6B",
                "--fcc-trigger-icon-display": "none",
                "--fcc-cta-bg": "#0B1F3F",
                "--fcc-cta-ink": "#FFFFFF",
                "--fcc-accent": "#C8102E",
                "--fcc-label-display": "block",
                "--fcc-modal-max-w": "420px",
                "--fcc-modal-radius": "16px",
                "--fcc-topbar-h": "44px",
                "--fcc-topbar-border": "0",
                "--fcc-topbar-title-display": "none",
                "--fcc-actions-border": "0",
                "--fcc-footer-pad-y": "0px",
                "--fcc-cta-width": "100%",
                "--fcc-cta-margin-top": "16px",
                "--fcc-check-justify": "center",
                "--fcc-actions-justify": "stretch",
                "--fcc-modal-shadow": "0 32px 80px rgba(0,0,0,0.45)",
                "--fcc-backdrop": "rgba(0,0,0,0.45)",
                "--fcc-topbar-pad-x": "12px",
                "--fcc-hero-pad-top": "30px",
                "--fcc-hero-gap": "14px",
                "--fcc-avatar-lg": "72px",
                "--fcc-name-font": "22px",
                "--fcc-body-pad-x": "28px",
                "--fcc-body-pad-bottom": "26px",
                "--fcc-field-gap": "12px",
                "--fcc-input-h": "46px",
                "--fcc-input-pad-x": "14px",
                "--fcc-input-radius": "10px",
                "--fcc-input-font": "15px",
                "--fcc-input-border": "#D8D2CD",
                "--fcc-input-placeholder": "#9A918B",
                "--fcc-cta-h": "48px",
                "--fcc-cta-pad-x": "24px",
                "--fcc-cta-radius": "999px",
                "--fcc-cta-font": "15px",
                "--fcc-checkbox-font": "11.5px",
                "--fcc-checkbox-gap": "9px",
                "--fcc-footer-pad-x": "28px",
                "--fcc-footer-pad-y": "16px",
            }}
        >
            {!widgetReady && placeholder}
            <div
                data-follow-creator
                data-client-id={clientId}
                data-user-id={userId}
                data-role-id={String(CREATOR_ROLE_ID)}
                data-creator-display-name={displayName || AmbassadorHelper.getDisplayName(ambassador) || ""}
                data-subtitle={SUBTITLE}
                data-creator-photo={AmbassadorHelper.getAmbassadorPhoto(ambassador, FOLLOW_AVATAR_PX)}
                data-api-env={env}
                style={widgetReady ? undefined : { position: "absolute", visibility: "hidden" }}
            />
        </Box>
    );
};

export default FollowCreator;
